/**
 * @fileoverview Manages the point creation and preview before we have enough points for our building.
 */

import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import { LineString } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';

import { AREA_CORNERS } from './tactical-annotation-calculator';

/**
 * Manages the point selection phase where users define the building corners.
 */
export class TacticalAnnotationPointManager {
  private clickedPoints: Coordinate[] = [];
  private previewFeature: Feature<LineString> | null = null;

  constructor(
    private readonly vectorSource: VectorSource,
  ) {}

  /**
   * Adds a clicked point while making the polygon
   */
  addPoint(coordinate: Coordinate): void {
    this.clickedPoints.push(coordinate);
  }

  /**
   * Clears the preview feature from the map.
   */
  clearPreview(): void {
    if (!this.previewFeature) {
      return;
    }

    this.vectorSource.removeFeature(this.previewFeature);
    this.previewFeature = null;
  }

  getClickedPoints(): Coordinate[] {
    return this.clickedPoints;
  }

  /**
   * Check that all building corners are clicked
   */
  hasFinishedPointSelection(): boolean {
    return this.clickedPoints.length === AREA_CORNERS;
  }

  reset(): void {
    this.clearPreview();
    this.clickedPoints = [];
  }

  /**
   * Updates the clicked points array (for example used when dragging point indicators).
   */
  updateClickedPoints(points: Coordinate[]): void {
    this.clickedPoints = points;
  }

  /**
   * Updates the preview line showing where the next segment will be.
   */
  updatePreview(currentCoordinate?: Coordinate): void {
    this.clearPreview();
    if (this.clickedPoints.length === 0) {
      return;
    }

    const previewCoordinates = currentCoordinate
      ? [...this.clickedPoints, currentCoordinate]
      : [...this.clickedPoints];

    // Only show preview if we have enough coordinates to display the line
    if (!currentCoordinate && previewCoordinates.length < 2) {
      return;
    }

    // After 3 points, show closing line back to start
    if (currentCoordinate && this.clickedPoints.length === 3) {
      previewCoordinates.push(this.clickedPoints[0]);
    }

    const previewFeature = new Feature({
      geometry: new LineString(previewCoordinates),
    });

    previewFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: '#007bff',
          width: 3,
          lineDash: [8, 8],
        }),
      }),
    );

    this.vectorSource.addFeature(previewFeature);
    this.previewFeature = previewFeature;
  }
}
